import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { comicDetails } from "@/mocks/comicDetail";

const chapterImages = [
  "https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20page%20in%20vertical%20scroll%20format%2C%20dynamic%20action%20scene%2C%20character%20fighting%20with%20special%20powers%2C%20dramatic%20lighting%2C%20cool%20tone%20colors%2C%20clean%20line%20art%2C%20speech%20bubbles%20in%20Korean%2C%20vertical%20composition&width=800&height=1200&seq=chapter-img-01&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20webtoon%20manhwa%20vertical%20page%20with%20dialogue%20scene%2C%20two%20characters%20talking%2C%20emotional%20expressions%2C%20indoor%20setting%2C%20warm%20lighting%2C%20clean%20manhwa%20art%20style%2C%20speech%20bubbles&width=800&height=1200&seq=chapter-img-02&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20page%20showing%20epic%20transformation%20scene%2C%20character%20powering%20up%20with%20glowing%20aura%2C%20energy%20effects%2C%20dark%20atmosphere%2C%20dramatic%20composition%2C%20vertical%20scroll%20format&width=800&height=1200&seq=chapter-img-03&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20webtoon%20page%20with%20romantic%20moment%2C%20two%20characters%20close%20together%2C%20soft%20blush%20expressions%2C%20gentle%20lighting%2C%20cherry%20blossom%20background%2C%20clean%20vertical%20manhwa%20style&width=800&height=1200&seq=chapter-img-04&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20action%20page%2C%20dramatic%20battle%20scene%2C%20explosion%20effects%2C%20dynamic%20camera%20angles%2C%20speed%20lines%2C%20intense%20manga%20style%2C%20vertical%20scroll%20composition&width=800&height=1200&seq=chapter-img-05&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20webtoon%20manhwa%20page%20with%20mysterious%20atmosphere%2C%20shadowy%20figure%20revealed%2C%20dramatic%20shadow%20play%2C%20dark%20tone%2C%20minimal%20dialogue%2C%20vertical%20format&width=800&height=1200&seq=chapter-img-06&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20webtoon%20page%20with%20beautiful%20scenery%2C%20character%20looking%20at%20sunset%2C%20warm%20golden%20light%2C%20emotional%20moment%2C%20cinematic%20composition%2C%20vertical%20manhwa%20art&width=800&height=1200&seq=chapter-img-07&orientation=portrait",
  "https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20cliffhanger%20page%2C%20shocking%20revelation%2C%20character%20surprised%20expression%2C%20dramatic%20impact%20lines%2C%20dark%20background%2C%20vertical%20scroll%20format&width=800&height=1200&seq=chapter-img-08&orientation=portrait",
];

export default function ChapterReadingPage() {
  const { slug, chapterId } = useParams<{ slug: string; chapterId: string }>();
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  const comic = slug ? comicDetails[slug] : undefined;
  const chNum = parseInt(chapterId || "1", 10);
  const currentChapter = comic?.chapters.find((ch) => ch.id === chNum);
  const prevChapter = comic?.chapters.find((ch) => ch.id === chNum - 1);
  const nextChapter = comic?.chapters.find((ch) => ch.id === chNum + 1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowControls(true);
  }, [slug, chapterId]);

  const handleScroll = useCallback(() => {
    setShowTopBtn(window.scrollY > 400);

    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-800 mb-2">章节未找到</h2>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className={`transition-all duration-300 ${
            showControls ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
          }`}
        >
          <div className="bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
            <div className="max-w-[800px] mx-auto px-4 h-14 flex items-center justify-between">
              <Link
                to={`/comic/${comic.slug}`}
                className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="line-clamp-1">{comic.title}</span>
              </Link>
              <span className="text-sm text-foreground-800 font-medium whitespace-nowrap">
                {currentChapter?.title || `第${chNum}话`}
              </span>
              <div className="flex items-center gap-3">
                <button className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors">
                  <i className="ri-settings-3-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors">
                  <i className="ri-list-unordered"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main ref={contentRef} className="bg-background-100 min-h-screen">
        <div className="max-w-[800px] mx-auto px-4 py-6" style={{ paddingTop: "3.5rem" }}>
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/comic/${comic.slug}`}
              className="hover:text-primary-500 transition-colors line-clamp-1"
            >
              {comic.title}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 whitespace-nowrap">
              {currentChapter?.title || `第${chNum}话`}
            </span>
          </nav>

          <div className="flex flex-col gap-2">
            {chapterImages.map((img, idx) => (
              <div key={idx} className="rounded-lg overflow-hidden bg-background-50">
                <img
                  src={img}
                  alt={`${comic.title} ${currentChapter?.title || `第${chNum}话`} 第${idx + 1}页`}
                  className="w-full h-auto"
                  title={`${comic.title} - 免费漫画下拉式观看 第${idx + 1}页`}
                  loading={idx < 2 ? undefined : "lazy"}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-8 mb-4">
            {prevChapter ? (
              <Link
                to={`/comic/${comic.slug}/chapter/${prevChapter.id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <div />
            )}
            {nextChapter ? (
              <Link
                to={`/comic/${comic.slug}/chapter/${nextChapter.id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <div />
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 pb-4">
            <button className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors">
              <i className="ri-thumb-up-line"></i>
            </button>
            <button className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors">
              <i className="ri-bookmark-line"></i>
            </button>
            <button className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors">
              <i className="ri-share-forward-line"></i>
            </button>
          </div>
        </div>
      </main>

      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-primary-500 text-background-50 flex items-center justify-center transition-all duration-300 hover:bg-primary-600 ${
          showTopBtn ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"
        }`}
        aria-label="回到顶部"
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </>
  );
}